import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChartColumn,
  Check,
  ChevronsUpDown,
  CreditCard,
  KeyRound,
  ListChecks,
  LogOut,
  Menu,
  Plus,
  Settings2,
  SquareTerminal,
  Users,
  X,
} from 'lucide-react';

import { asApiError } from '../lib/api';
import { money, monthName } from '../lib/format';
import { useBilling, useSession } from '../lib/session';
import type { Role, TeamKind } from '../lib/types';
import { VerifyBanner } from '../pages/Verify';
import { CreateOrganization } from './CreateOrganization';
import { BILLING_PATH, Brand, Mark, Spinner, sentence } from './ui';

const GROUPS = [
  {
    label: 'Workloads',
    items: [
      { to: '/sandboxes', label: 'Sandboxes', icon: SquareTerminal },
      { to: '/ci', label: 'CI', icon: ListChecks },
    ],
  },
  {
    label: 'Organization',
    items: [
      { to: '/keys', label: 'API keys', icon: KeyRound },
      { to: '/members', label: 'Members', icon: Users },
      { to: '/settings', label: 'Settings', icon: Settings2, end: true },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/usage', label: 'Usage', icon: ChartColumn },
      { to: BILLING_PATH, label: 'Billing', icon: CreditCard },
    ],
  },
] as const;

/** Credit left while the credit lasts and there is no card; a nudge when there is neither; this month's spend after. */
function MoneyChip() {
  const { billing } = useBilling();
  if (!billing) {
    return (
      <div className="chip" aria-hidden="true">
        <span className="skeleton" style={{ width: '70%' }} />
      </div>
    );
  }
  const onCredit = billing.card === null && billing.credit.remainingUsd > 0;
  if (onCredit) {
    const share = billing.credit.grantedUsd > 0 ? billing.credit.remainingUsd / billing.credit.grantedUsd : 0;
    return (
      <Link className="chip" to={BILLING_PATH} aria-label={`Credit: ${money(billing.credit.remainingUsd)} left. Open billing.`}>
        <span className="chip__row">
          <span className="chip__label">Credit</span>
          <span className="chip__value">{money(billing.credit.remainingUsd)}</span>
        </span>
        <span className="chip__meter" aria-hidden="true">
          <span style={{ width: `${Math.max(2, share * 100)}%` }} />
        </span>
        <span className="chip__note">left of {money(billing.credit.grantedUsd)}</span>
      </Link>
    );
  }
  /* An organization starts with no credit, and a personal account can spend its own: either way work waits for a card. */
  if (billing.card === null && billing.mode !== 'off') {
    return (
      <Link className="chip" to={BILLING_PATH} aria-label="No card on file. Add one to run work.">
        <span className="chip__row">
          <span className="chip__label">No card yet</span>
        </span>
        <span className="chip__note" style={{ color: 'var(--ink)', fontWeight: 500 }}>
          Add a card to run work
        </span>
      </Link>
    );
  }
  const limit = billing.budget.limitUsd;
  const share = limit ? billing.budget.spentUsd / limit : null;
  const tone = share === null ? '' : share >= 1 ? 'is-over' : share >= 0.8 ? 'is-warn' : '';
  return (
    <Link className="chip" to="/usage" aria-label={`${monthName(billing.period.id)} spend: ${money(billing.period.spentUsd)}. Open usage.`}>
      <span className="chip__row">
        <span className="chip__label">{monthName(billing.period.id).split(' ')[0]} so far</span>
        <span className="chip__value">{money(billing.period.spentUsd)}</span>
      </span>
      {share !== null ? (
        <span className="chip__meter" aria-hidden="true">
          <span className={tone} style={{ width: `${Math.min(100, Math.max(2, share * 100))}%` }} />
        </span>
      ) : null}
      <span className="chip__note" style={billing.status === 'past_due' ? { color: 'var(--refused)', fontWeight: 500 } : undefined}>
        {billing.status === 'past_due' ? 'Payment failed: update your card' : limit ? `of ${money(limit)} limit` : 'No spending limit'}
      </span>
    </Link>
  );
}

/** The team block: switches between the personal account and organizations, and makes new organizations. */
function TeamSwitcher() {
  const { me, switchTeam } = useSession();
  const { reloadBilling } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  if (!me) return null;
  const teamName = me.team.name;
  const badge = (name: string) => (
    <span className="team__badge" aria-hidden="true">
      {name.trim().charAt(0) || '·'}
    </span>
  );
  /* A personal account says so; in an organization what matters is your role. */
  const sub = (kind: TeamKind, role: Role) => (kind === 'personal' ? 'Personal' : role === 'owner' ? 'Owner' : 'Member');
  const body = (
    <span style={{ minWidth: 0 }}>
      <span className="team__name">{teamName}</span>
      <span className="team__role">{sub(me.team.kind, me.role)}</span>
    </span>
  );

  const choose = async (teamId: string) => {
    if (teamId === me.team.id) {
      setOpen(false);
      return;
    }
    setBusy(teamId);
    setError(null);
    try {
      await switchTeam(teamId);
      void reloadBilling();
      setOpen(false);
      // A task or sandbox page belongs to the old team: go to its list instead.
      const detail = location.pathname.match(/^\/(ci|sandboxes)\/[^/]+/);
      if (detail) navigate(`/${detail[1]}`);
    } catch (err) {
      setError(sentence(asApiError(err).message));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="teams" ref={wrap}>
      <button ref={button} type="button" className="team team--switch" aria-expanded={open} aria-controls="team-list" onClick={() => setOpen((v) => !v)} title={teamName}>
        {badge(teamName)}
        {body}
        <ChevronsUpDown className="team__chev" aria-hidden="true" />
      </button>
      {open ? (
        <div className="teams__pop" id="team-list">
          <div className="teams__label">Switch to</div>
          <ul>
            {me.teams.map((t) => {
              const current = t.id === me.team.id;
              return (
                <li key={t.id}>
                  <button type="button" className="teams__item" aria-current={current || undefined} disabled={busy !== null} onClick={() => void choose(t.id)}>
                    {badge(t.name)}
                    <span style={{ minWidth: 0 }}>
                      <span className="team__name">{t.name}</span>
                      <span className="team__role">{sub(t.kind, t.role)}</span>
                    </span>
                    {busy === t.id ? <Spinner /> : current ? <Check className="teams__check" aria-label="Current team" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {error ? (
            <p className="teams__error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="teams__foot">
            <button
              type="button"
              className="teams__action"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
            >
              <Plus aria-hidden="true" />
              Create organization
            </button>
            <Link className="teams__action" to="/settings" onClick={() => setOpen(false)}>
              <Settings2 aria-hidden="true" />
              Settings
            </Link>
          </div>
        </div>
      ) : null}
      <CreateOrganization open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

export function Shell() {
  const { me, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);
  const sideRef = useRef<HTMLElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    sideRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const teamName = me?.team.name ?? '';

  return (
    <div className="app">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <div className="topbar__left">
          <Link to="/" aria-label="offstage cloud, Sandboxes">
            <Mark />
          </Link>
          <span className="topbar__team">{teamName}</span>
        </div>
        <button
          ref={menuButton}
          type="button"
          className="btn btn--quiet btn--icon"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="sidebar"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </header>
      <button type="button" className={`drawer-scrim${open ? ' is-open' : ''}`} aria-hidden="true" tabIndex={-1} onClick={() => setOpen(false)} />

      <aside id="sidebar" ref={sideRef} className={`side${open ? ' is-open' : ''}`} aria-label="Console">
        <div className="side__top">
          <Brand />
          <TeamSwitcher />
        </div>
        <nav className="nav" aria-label="Main">
          {GROUPS.map((group, i) => (
            <div className="nav__group" key={group.label ?? i} style={i === 0 ? { marginTop: 0 } : undefined}>
              {group.label ? <div className="nav__label">{group.label}</div> : null}
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={'end' in item ? item.end : false}>
                  <item.icon aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="side__foot">
          <MoneyChip />
          <nav className="nav" style={{ padding: 0 }} aria-label="Help">
            <NavLink to="/docs">
              <BookOpen aria-hidden="true" />
              Docs
            </NavLink>
          </nav>
          <div className="account">
            <span className="account__email" title={me?.user.email}>
              {me?.user.email}
            </span>
            <button type="button" className="btn btn--quiet btn--icon" onClick={() => void signOut()} aria-label="Sign out" title="Sign out">
              <LogOut aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <main className="main" id="main" tabIndex={-1}>
        <VerifyBanner />
        <div className="frame">
          {/* Keyed by team: switching teams remounts the page, so everything refetches for the new one. */}
          <Outlet key={me?.team.id} />
        </div>
      </main>
    </div>
  );
}
